import { useTenantsState } from '@frontegg/react';
import { useAtomValue } from 'jotai';
import { isDebugMode } from '../../../debug/debugUser';
import { customerTenantIdState } from '../../../services/state/AuthState';
import { AgentWsIdentityAtom } from './chatActionHooks';

export interface AgentTenantMismatch {
  agentTenantId: string;
  sessionTenantId: string;
  agentTenantName?: string;
  sessionTenantName?: string;
}

/**
 * Compares the tenant the agent WS connection is bound to (reported by the
 * server in the `connection_established` frame) against the customer tenant
 * this host belongs to. The two differ when the socket connected before the
 * customer was known, so it bound itself to the token's tenant, or when the
 * agent build ignores the tenant attribute; either way the conversation list
 * the socket delivered belongs to another tenant.
 *
 * Returns null while either side is unknown, so the banner never flashes
 * during initial load or against older agent deployments that don't report a
 * tenant.
 */
/* eslint-disable react-hooks/rules-of-hooks -- isDebugMode is constant for the app lifetime (same pattern as useAccountSwitcher) */
export function useAgentTenantMismatch(): AgentTenantMismatch | null {
  if (isDebugMode()) {
    return null;
  }
  const { tenants } = useTenantsState();
  const sessionTenantId = useAtomValue(customerTenantIdState);
  const wsIdentity = useAtomValue(AgentWsIdentityAtom);

  const agentTenantId = wsIdentity?.tenantId;
  if (!sessionTenantId || !agentTenantId || sessionTenantId === agentTenantId) {
    return null;
  }
  return {
    agentTenantId,
    sessionTenantId,
    agentTenantName: tenants?.find((tenant) => tenant.tenantId === agentTenantId)?.name,
    sessionTenantName: tenants?.find((tenant) => tenant.tenantId === sessionTenantId)?.name,
  };
}
/* eslint-enable react-hooks/rules-of-hooks */
